import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MicroServiceHttpService } from './microServiceHttp.service';

export interface AgentSchedule {
  pid: number;
  chatbotId: number;
  nodeId: number;
  nodeName?: string;
  name: string;
  triggerPrompt: string;
  cronExpr: string;
  timezone: string;
  isActive: boolean;
  avgDurationMs: number | null;
  lastRunAt: string | null;
  lastRunStatus: 'SUCCESS' | 'FAILED' | null;
  runCount: number;
  successCount: number;
  maxRetries: number;
  retryIntervalMins: number;
  upstreamWorkflowIds?: number[];
  createdAt: string;
}

export interface TokenBreakdownEntry {
  agentName: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  toolCallCount?: number;
}

export interface ScheduleRunLog {
  pid: number;
  scheduleId: number;
  chatbotId: number;
  nodeId: number;
  firedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';
  /** Trigger prompt that was sent to the agent for this run */
  runInput: string | null;
  output: string | null;
  errorMessage: string | null;
  /** True when this run reused cached/consumed upstream summaries (no upstream tool calls during this run). */
  usedCachedUpstream?: boolean | null;
  /** Earliest upstream window_end_at used by this run (UTC). */
  upstreamUsedWindowMinEndAt?: string | null;
  /** Latest upstream window_end_at used by this run (UTC). */
  upstreamUsedWindowMaxEndAt?: string | null;
  /** How many upstream windows were consumed/reused by this run. */
  upstreamUsedWindowsCount?: number | null;
  /** Upstream wl_bot_schedule_run_logs.pid values consumed/reused by this run. */
  upstreamUsedLogPids?: number[] | null;
  tokenUsage: { inputTokens?: number; outputTokens?: number; totalTokens?: number } | null;
  /** Per-agent token breakdown: [{agentName, inputTokens, outputTokens, totalTokens}] */
  tokenBreakdown: TokenBreakdownEntry[] | null;
  traceId: string | null;
  attemptNumber: number;
  isRetry: boolean;
  parentLogId: number | null;
  /** Snapshot of agent-level logs stored as JSON */
  subAgentLogs?: any[] | null;
  /** Pre-loaded node executions from optimized API */
  nodeExecutions?: DagNodeExecution[];
}

export interface DagNodeExecution {
  pid: number;
  executionId: string;
  nodeId: number;
  nodeName: string;
  executionOrder: number;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'SKIPPED' | 'RETRYING';
  /** The exact query / variables sent into this node */
  inputPayload: any;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  durationMs: number | null;
  retryCount: number;
  errorCode: string | null;
  errorMessage: string | null;
  isStale: boolean | null;
  dataFingerprint: string | null;
  startedAt: string;
  completedAt: string | null;
}

export type DeliveryChannelType = 'EMAIL' | 'GOOGLE_CHAT';

export interface DeliveryChannel {
  pid: number;
  type: DeliveryChannelType;
  config: Record<string, any>;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryConfig {
  chatbotId: number;
  channels: DeliveryChannel[];
}

export interface PaginationInfo {
  totalRecords: number;
  limit: number;
  offset: number;
  totalPages: number;
  currentPage: number;
  hasNext: boolean;
  hasPrev: boolean;
}

function unwrapApiPayload<T>(raw: unknown): T | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  if ('data' in o && o['data'] != null) {
    return o['data'] as T;
  }

  return raw as T;
}

function normalizeRunLogsResponse(
  raw: unknown,
): {
  data: ScheduleRunLog[];
  total: number;
  limit: number;
  offset: number;
  totalTokensUsed: number;
  pageTokensUsed: number;
  pagination: PaginationInfo;
} {
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const inner = unwrapApiPayload<unknown>(raw);
    const buildPagination = (
      total: number,
      limit: number,
      offset: number,
      maybePagination?: Record<string, unknown>,
    ): PaginationInfo => {
      const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)));
      const currentPage = Math.floor(offset / Math.max(limit, 1)) + 1;
      return {
        totalRecords: total,
        limit,
        offset,
        totalPages,
        currentPage,
        hasNext:
          typeof maybePagination?.['hasNext'] === 'boolean'
            ? (maybePagination['hasNext'] as boolean)
            : offset + limit < total,
        hasPrev:
          typeof maybePagination?.['hasPrev'] === 'boolean'
            ? (maybePagination['hasPrev'] as boolean)
            : offset > 0,
      };
    };

    if (Array.isArray(o['data'])) {
      const data = o['data'] as ScheduleRunLog[];
      const total = typeof o['total'] === 'number' ? o['total'] : data.length;
      const limit = typeof o['limit'] === 'number' ? o['limit'] : data.length;
      const offset = typeof o['offset'] === 'number' ? o['offset'] : 0;
      const totalTokensUsed =
        typeof o['totalTokensUsed'] === 'number' ? o['totalTokensUsed'] : 0;
      const pageTokensUsed =
        typeof o['pageTokensUsed'] === 'number'
          ? o['pageTokensUsed']
          : data.reduce((sum, log) => sum + (log.tokenUsage?.totalTokens ?? 0), 0);
      const pagination =
        o['pagination'] && typeof o['pagination'] === 'object'
          ? buildPagination(total, limit, offset, o['pagination'] as Record<string, unknown>)
          : buildPagination(total, limit, offset);
      return {
        data,
        total,
        limit,
        offset,
        totalTokensUsed,
        pageTokensUsed,
        pagination,
      };
    }

    if (inner && typeof inner === 'object') {
      const i = inner as Record<string, unknown>;
      const data = Array.isArray(i['data']) ? (i['data'] as ScheduleRunLog[]) : [];
      const total = typeof i['total'] === 'number' ? i['total'] : data.length;
      const limit = typeof i['limit'] === 'number' ? i['limit'] : data.length;
      const offset = typeof i['offset'] === 'number' ? i['offset'] : 0;
      const totalTokensUsed =
        typeof i['totalTokensUsed'] === 'number' ? i['totalTokensUsed'] : 0;
      const pageTokensUsed =
        typeof i['pageTokensUsed'] === 'number'
          ? i['pageTokensUsed']
          : data.reduce((sum, log) => sum + (log.tokenUsage?.totalTokens ?? 0), 0);
      const pagination =
        i['pagination'] && typeof i['pagination'] === 'object'
          ? buildPagination(total, limit, offset, i['pagination'] as Record<string, unknown>)
          : buildPagination(total, limit, offset);
      return {
        data,
        total,
        limit,
        offset,
        totalTokensUsed,
        pageTokensUsed,
        pagination,
      };
    }
  }

  if (Array.isArray(raw)) {
    const total = raw.length;
    const limit = raw.length;
    const offset = 0;
    return {
      data: raw as ScheduleRunLog[],
      total,
      limit,
      offset,
      totalTokensUsed: 0,
      pageTokensUsed: 0,
      pagination: {
        totalRecords: total,
        limit,
        offset,
        totalPages: 1,
        currentPage: 1,
        hasNext: false,
        hasPrev: false,
      },
    };
  }

  return {
    data: [],
    total: 0,
    limit: 0,
    offset: 0,
    totalTokensUsed: 0,
    pageTokensUsed: 0,
    pagination: {
      totalRecords: 0,
      limit: 0,
      offset: 0,
      totalPages: 1,
      currentPage: 1,
      hasNext: false,
      hasPrev: false,
    },
  };
}

function normalizeArrayResponse<T>(raw: unknown): T[] {
  const body = unwrapApiPayload<unknown>(raw);

  if (Array.isArray(body)) {
    return body as T[];
  }

  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    if (Array.isArray(o['data'])) {
      return o['data'] as T[];
    }
  }

  return [];
}

@Injectable({ providedIn: 'root' })
export class ScheduleAgentService {
  private http = inject(MicroServiceHttpService);

  getSchedules(chatbotId: number): Observable<{ data: AgentSchedule[] }> {
    return this.http
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/schedules`, {
        showLoader: false,
        showToast: false,
      })
      .pipe(map((raw) => ({ data: normalizeArrayResponse<AgentSchedule>(raw) })));
  }

  getRunLogs(
    chatbotId: number,
    limit = 10,
    offset = 0,
  ): Observable<{
    data: {
      data: ScheduleRunLog[];
      total: number;
      limit: number;
      offset: number;
      totalTokensUsed: number;
      pageTokensUsed: number;
      pagination: PaginationInfo;
    };
  }> {
    return this.http
      .get<unknown>(
        'whitelabel',
        `chatbot/bot/${chatbotId}/schedule-run-logs?limit=${limit}&offset=${offset}`,
        {
          showLoader: false,
          showToast: false,
        },
      )
      .pipe(map((raw) => ({ data: normalizeRunLogsResponse(raw) })));
  }

  getRunLogsBySchedule(scheduleId: number, limit = 20): Observable<{ data: ScheduleRunLog[] }> {
    return this.http
      .get<unknown>(
        'whitelabel',
        `chatbot/schedules/${scheduleId}/run-logs?limit=${limit}`,
        {
          showLoader: false,
        },
      )
      .pipe(map((raw) => ({ data: normalizeArrayResponse<ScheduleRunLog>(raw) })));
  }

  getDagExecutions(chatbotId: number, executionId: string): Observable<{ data: DagNodeExecution[] }> {
    return this.http
      .get<unknown>(
        'whitelabel',
        `chatbot/bot/${chatbotId}/dag-executions/${executionId}`,
        {
          showLoader: false,
        },
      )
      .pipe(map((raw) => ({ data: normalizeArrayResponse<DagNodeExecution>(raw) })));
  }

  toggleSchedule(scheduleId: number, isActive: boolean): Observable<any> {
    return this.http.patch('whitelabel', `chatbot/schedules/${scheduleId}/toggle`, { isActive }, {
      showLoader: false,
      showToast: true,
      successMessage: isActive ? 'Schedule enabled' : 'Schedule disabled',
    });
  }

  deleteSchedule(chatbotId: number, scheduleId: number): Observable<void> {
    return this.http.delete<void>('whitelabel', `chatbot/bot/${chatbotId}/schedules/${scheduleId}`, {
      showLoader: false,
      showToast: true,
      successMessage: 'Schedule deleted',
    });
  }

  /** Returns delivery channel configuration for a chatbot. */
  getDeliveryConfig(chatbotId: number): Observable<DeliveryConfig> {
    return this.http
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/delivery-config`, {
        showLoader: false,
        showToast: false,
      })
      .pipe(
        map((raw) => {
          const body = unwrapApiPayload<DeliveryConfig>(raw);
          return body ?? { chatbotId, channels: [] };
        }),
      );
  }

  /** Saves (replaces) delivery channel configuration for a chatbot. */
  saveDeliveryConfig(
    chatbotId: number,
    channels: {
      type: DeliveryChannelType;
      config: Record<string, any>;
      enabled: boolean;
    }[],
  ): Observable<DeliveryConfig> {
    return this.http
      .post<unknown>(
        'whitelabel',
        `chatbot/bot/${chatbotId}/delivery-config`,
        { channels },
        { showLoader: false, showToast: true, successMessage: 'Delivery config saved' },
      )
      .pipe(
        map((raw) => {
          const body = unwrapApiPayload<DeliveryConfig>(raw);
          return body ?? { chatbotId, channels: [] };
        }),
      );
  }

  /** Sends a test delivery to all configured channels. */
  testDelivery(chatbotId: number): Observable<any> {
    return this.http.post(
      'whitelabel',
      `chatbot/bot/${chatbotId}/delivery-config/test`,
      {},
      { showLoader: false, showToast: true, successMessage: 'Test delivery sent' },
    );
  }

  /**
   * Upsert schedule for a specific workflow node.
   * If `draft.pid` is set the backend updates that row; otherwise it creates a new one.
   */
  upsertNodeSchedule(
    chatbotId: number,
    nodeId: number,
    draft: {
      pid: number | null;
      name: string;
      triggerPrompt: string;
      cronExpr: string;
      timezone: string;
      isActive: boolean;
      maxRetries: number;
      retryIntervalMins: number;
      upstreamWorkflowIds: number[];
    },
  ): Observable<{ data: AgentSchedule }> {
    const body = {
      nodeId,
      name: draft.name,
      triggerPrompt: draft.triggerPrompt,
      cronExpr: draft.cronExpr,
      timezone: draft.timezone,
      isActive: draft.isActive,
      maxRetries: draft.maxRetries,
      retryIntervalMins: draft.retryIntervalMins,
      upstreamWorkflowIds: draft.upstreamWorkflowIds,
    };
    if (draft.pid != null) {
      // The gateway never served an update; prism-new's is PUT under the chatbot.
      return this.http.put('whitelabel', `chatbot/bot/${chatbotId}/schedules/${draft.pid}`, body, {
        showLoader: false,
      }) as Observable<{ data: AgentSchedule }>;
    }
    return this.http.post('whitelabel', `chatbot/bot/${chatbotId}/schedules`, body, {
      showLoader: false,
    }) as Observable<{ data: AgentSchedule }>;
  }
}
